/**
 * A Membrane connection can be updated in place when it is still on an older
 * version of the same connector the integration currently uses. A different
 * connector id is a migration, not this update. Missing pins are unknown.
 */

export type ConnectionVersionPins = {
  integrationId?: string | null;
  connectorId?: string | null;
  connectorVersion?: string | null;
};

export type IntegrationVersionPin = {
  id: string;
  connectorId?: string | null;
  connectorVersion?: string | null;
};

export type ConnectionVersionAction = "update" | "reconnect" | "none";

export const connectionVersionUpdateCopy =
  "A newer connector version is available for this account.";

export const connectionVersionUpdateLabel = "Update connection";

export const connectionVersionUpdatingLabel = "Updating…";

export const connectionVersionUpdateSuccess = "Connection updated.";

export function connectionVersionUpdateFailure(message: string): string {
  return `Couldn't update connection: ${message}`;
}

const READY = "READY";

function present(value: string | null | undefined): value is string {
  return typeof value === "string" && value.length > 0;
}

/** Membrane treats a missing connection state as ready. */
export function isAccountReady(state: string | null | undefined): boolean {
  return (state ?? READY) === READY;
}

/**
 * True only when the connection belongs to this integration element, names the
 * same connector, and names a different version. Both pins must be present.
 */
export function isConnectionVersionOutdated(
  connection: ConnectionVersionPins,
  integration: IntegrationVersionPin
): boolean {
  if (connection.integrationId !== integration.id) return false;
  const connectionConnectorId = connection.connectorId;
  const integrationConnectorId = integration.connectorId;
  const connectionVersion = connection.connectorVersion;
  const integrationVersion = integration.connectorVersion;
  if (
    !present(connectionConnectorId) ||
    !present(integrationConnectorId) ||
    !present(connectionVersion) ||
    !present(integrationVersion)
  ) {
    return false;
  }
  if (connectionConnectorId !== integrationConnectorId) return false;
  return connectionVersion !== integrationVersion;
}

/**
 * The account-row action for connector versioning.
 *
 * An account that is not ready keeps Reconnect. A ready account on an older
 * version of the same connector gets Update. Anything else, including a
 * different connector or missing pins, gets no version action.
 */
export function connectionVersionAction(args: {
  state: string | null | undefined;
  connection: ConnectionVersionPins;
  integration: IntegrationVersionPin;
}): ConnectionVersionAction {
  if (!isAccountReady(args.state)) return "reconnect";
  if (isConnectionVersionOutdated(args.connection, args.integration)) {
    return "update";
  }
  return "none";
}

/**
 * Moves an existing connection onto the integration's current connector
 * version. Refuses to run unless {@link isConnectionVersionOutdated} is true,
 * and does not refresh when Membrane returns a different connection id.
 */
export async function patchConnectionConnectorVersion(args: {
  connectionId: string;
  connection: ConnectionVersionPins;
  integration: IntegrationVersionPin;
  patch: (body: { connectorVersion: string }) => Promise<{ id: string }>;
  refresh: () => Promise<void>;
}): Promise<boolean> {
  const version = args.integration.connectorVersion;
  if (
    !present(version) ||
    !isConnectionVersionOutdated(args.connection, args.integration)
  ) {
    return false;
  }
  const updated = await args.patch({ connectorVersion: version });
  if (updated.id !== args.connectionId) {
    throw new Error(
      "Membrane connection update returned a different connection id"
    );
  }
  await args.refresh();
  return true;
}

/**
 * Runs one in-place version update and reports the outcome.
 *
 * A call for a connection that is already updating returns without patching.
 * Success is reported only after `update` resolves true, which is after the
 * connection has been refreshed. A failure is reported and the connection is
 * cleared from the in-flight set, so the action can be tried again.
 */
export async function runConnectionVersionUpdate(args: {
  connectionId: string;
  isUpdating: (connectionId: string) => boolean;
  markUpdating: (connectionId: string) => void;
  clearUpdating: (connectionId: string) => void;
  update: () => Promise<boolean>;
  notify: (message: string) => void;
  describeError: (error: unknown) => string;
}): Promise<void> {
  if (args.isUpdating(args.connectionId)) return;
  args.markUpdating(args.connectionId);
  try {
    const updated = await args.update();
    if (updated) args.notify(connectionVersionUpdateSuccess);
  } catch (error) {
    args.notify(connectionVersionUpdateFailure(args.describeError(error)));
  } finally {
    args.clearUpdating(args.connectionId);
  }
}
